import React from "react";
import { FileText, ArrowRight, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

const CONSTITUTION_URL =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/ijk7menv_BridgeColumbia%20Constitution.docx";

const HIGHLIGHTS = [
  { title: "Article I · Name", body: "Defines the organization as BridgeColumbia and establishes the Executive Board." },
  { title: "Article II · Purpose", body: "To foster constructive dialogue, promote understanding across differing viewpoints, and host events (speakers, activities, moderated discussions) that support this mission." },
  { title: "Article III · Membership", body: "Open to registered Columbia students, faculty, and staff — with the general body at least three-fourths undergraduate. Members are expected to honor the purpose and attend events. Membership may be revoked by the President or a Board majority for repeated disregard of the discussion norms." },
  { title: "Article IV · Events & Procedures", body: "Events are organized by the Board at a minimum weekly cadence. Board meetings are weekly, and Board decisions run on consensus with formal votes decided by simple majority." },
  { title: "Article V · Officers", body: "The Board consists of the President, Vice President, Director of Strategy, Secretary, Treasurer, Director of Outreach, and Director of Communications. Any position may have a co-lead. Duties for each role are detailed inside." },
  { title: "Article VI · Elections & Removal", body: "Elections happen at the end of each academic year; candidates must have been members for at least one semester. Officers may be impeached for egregious conduct or neglect by a simple majority of both the membership and the Board (minus the impeached member). Mid-term vacancies are filled by volunteer with a Board vote if needed." },
  { title: "Article VII · Committees", body: "Standing committees (e.g., Outreach, Communications, Finance) are formed or dissolved by a simple Board majority. Each committee is overseen by the matching Officer; committee members attend Board meetings only when invited." },
  { title: "Article VIII · By-Laws", body: "By-laws for the organization and the Board are established or altered by a two-thirds Board vote, with proposals submitted in writing and distributed at least two weeks in advance." },
  { title: "Article IX · Amendments", body: "The Constitution may be amended by the Board or by petition of at least one-third of the membership. Adoption requires a two-thirds majority of the voting membership." },
  { title: "Article X · Ratification", body: "This Constitution is established by a two-thirds majority vote of the BridgeColumbia membership." },
];

export default function Constitution() {
  return (
    <div>
      <section className="relative pt-16 sm:pt-24 pb-8 sm:pb-14 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Policies & Procedures</p>
          <h1 className="font-serif-display text-[56px] md:text-[96px] font-bold leading-[0.98] text-white tracking-tight">
            The <span className="hl-block-deep">Constitution.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-slate-200 font-serif-body text-lg leading-relaxed">
            The full governing document of BridgeColumbia — covering our name, purpose, membership, officers, discussion norms, and amendment procedure. Download the complete text below.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={CONSTITUTION_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-cyan-300 hover:bg-cyan-200 text-[#062a40] px-5 py-2.5 font-medium transition-colors btn-shine"
            >
              <FileText className="w-4 h-4" /> Download the Constitution
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full border border-cyan-300/70 hover:border-cyan-200 px-5 py-2.5 text-slate-100 hover:bg-cyan-300/10 transition-colors"
            >
              Read our mission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Overview</p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-semibold text-white leading-[1.02]">
            What the Constitution <span className="hl-block-deep">covers.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-slate-300 font-serif-body">
            A high-level tour of the sections you’ll find inside. For exact wording, definitions, and voting procedures, refer to the downloadable document.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <article
                key={i}
                className="rounded-2xl border border-white/15 bg-[#145074] p-7 hover:border-cyan-300/50 hover:bg-[#1a618d] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-cyan-200" />
                </div>
                <h3 className="mt-5 font-serif-display text-2xl text-white leading-tight">{h.title}</h3>
                <p className="mt-3 text-slate-200 font-serif-body leading-relaxed">{h.body}</p>
              </article>
            ))}
          </div>

          <p className="mt-10 text-sm text-slate-400 italic">
            Note: this overview is a plain-language snapshot. The downloadable Constitution governs in case of any discrepancy.
          </p>
        </div>
      </section>
    </div>
  );
}
